// frontend/src/components/RepCalculator.js
//
// The "My Calculator" tab on the rep dashboard. Two sections:
//
//   My Math    — the payout arithmetic for each month of the quarter, written out
//                step by step so a rep can check their own commission by hand.
//   My Sandbox — the same arithmetic replayed with hypothetical GP layered on:
//                pull in pending shipments, or add a deal you're chasing, and see
//                what the quarter would pay.
//
// IMPORTANT: this file does NO commission math. Every dollar figure shown comes
// from /api/reps/me/math and /api/reps/me/sandbox, which run the same engine
// functions that produce the real payouts. That's deliberate — a calculator that
// re-implemented the formula in the browser could agree with itself and still
// disagree with the paycheck, which would defeat the purpose of showing the work.

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { getRepMath, getRepPending, runRepSandbox } from '../api/client';

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmt = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n);

const fmt0 = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(n);

// Signed, for deltas: "+$4,200.00" / "−$310.00" (true minus sign, not a hyphen).
const fmtDelta = (n) => {
  if (n == null) return '—';
  if (Math.abs(n) < 0.005) return '$0.00';
  return (n > 0 ? '+' : '−') + fmt(Math.abs(n));
};

const pct = (r) => r == null ? '—' : `${(r * 100).toFixed(r * 100 % 1 === 0 ? 0 : 1)}%`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
}) : '—';

const bandRange = (b) => {
  if (b.to == null) return `${fmt0(b.from)}+`;
  if (b.from === 0) return `Below ${fmt0(b.to)}`;
  return `${fmt0(b.from)} – ${fmt0(b.to - 1)}`;
};

// ─── Entry point ──────────────────────────────────────────────────────────────
export default function RepCalculator({ quarter, repId = null }) {
  const { data: math, loading, error } = useApi(() => getRepMath(quarter, repId), [quarter, repId]);

  if (loading) return <CalcSkeleton />;
  if (error)   return <Notice tone="red">Couldn't load your calculation: {error}</Notice>;
  if (!math)   return <Notice tone="muted">No commission data available for this quarter.</Notice>;

  return (
    <div>
      <MyMath math={math} />
      <MySandbox quarter={math.quarter} repId={repId} math={math} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MY MATH
// ═══════════════════════════════════════════════════════════════════════════════
function MyMath({ math }) {
  const { months, totals, bands, rates, quarter, persistedTotal, plan } = math;

  // Is the Managed Services plan in play for this rep / quarter?
  const msInPlay = plan?.current === 'msa' || months.some(m => m.ms?.gp > 0);
  const aeInPlay = plan?.current !== 'msa' || months.some(m => m.hwSw.gpThisMonth > 0 || m.services.gp > 0 || m.fod.gp > 0);

  // A month with no GP in any stream has no arithmetic worth walking through.
  const active = months.filter(m =>
    m.hwSw.gpThisMonth > 0 || m.services.gp > 0 || m.fod.gp > 0 || (m.ms?.gp > 0) || m.persisted
  );

  // Approved/paid months stop recalculating, so a later sync can move GP and leave
  // the locked figure behind. Surface that instead of letting the two disagree
  // silently — the rep is here precisely to reconcile the two.
  const drifted = months.filter(m => m.matchesPersisted === false);

  return (
    <section style={{ marginBottom: '40px' }}>
      <SectionHeading
        eyebrow="Show your work"
        title="My Math"
        blurb={`Every dollar of your ${quarter} commission, calculated line by line. These are the
                exact steps the system runs — check them against your own numbers.`}
      />

      {plan?.assignments?.length > 0 && (
        <Notice tone="muted">
          <strong>Which plan pays a deal is decided by its close date.</strong>{' '}
          {[...plan.assignments].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom)).map((a, i) => (
            <span key={a.effectiveFrom}>{i > 0 ? ' · ' : ''}<strong>{plan.labels?.[a.plan] || a.plan}</strong> for deals closed on/after {fmtDate(a.effectiveFrom)}</span>
          ))}
          {' '}· AE plan before that. The month it pays is still the month it ships. A month with deals from both plans shows both sets of math below.
        </Notice>
      )}

      {msInPlay && (
        <div className="card" style={{ padding: '20px 24px', marginBottom: '20px', borderColor: 'rgba(57, 185, 223, 0.45)' }}>
          <div className="label" style={{ marginBottom: '14px' }}>Managed Services plan — flat rates, no tiers, no accumulator</div>
          <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
            <StreamNote title="New MSA"      rate={pct(plan.msRates?.MSA)}         color="var(--blue-sky)" body="Managed Services (MSA) GP fulfilled this month." />
            <StreamNote title="Renewal MSA"  rate={pct(plan.msRates?.RENEWAL_MSA)} color="var(--blue-sky)" body="Renewal MSA GP fulfilled this month." />
            <StreamNote title="Services"     rate={pct(plan.msRates?.SERVICES)}    color="var(--green)"    body="Professional Services / Projects, incl. ELM and cabling labor." />
            <StreamNote title="HW/SW"        rate={pct(plan.msRates?.HWSW)}        color="var(--blue-primary)" body="Hardware and Software, flat. No quarterly tiers on this plan." />
            <StreamNote title="FOD"          rate={pct(plan.msRates?.FOD)}         color="var(--amber)"    body="Paid like hardware on this plan." />
          </div>
        </div>
      )}

      {/* How the AE plan works — the three streams, up front */}
      {aeInPlay && <div className="card" style={{ padding: '20px 24px', marginBottom: '20px' }}>
        <div className="label" style={{ marginBottom: '14px' }}>{msInPlay ? 'AE plan — the three streams' : 'The three streams that pay you'}</div>
        <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
          <StreamNote
            title="HW/SW"
            rate="4% → 12%"
            color="var(--blue-primary)"
            body={`Accumulates across the quarter. Cross a tier and your NEW rate applies retroactively to every dollar of quarter GP — not just the dollars above the line.${rates.fodHwSwCutover ? ` Includes FOD on deals closed on/after ${fmtDate(rates.fodHwSwCutover)}.` : ''}`}
          />
          <StreamNote
            title="Services & Managed Services"
            rate={pct(rates.services)}
            color="var(--green)"
            body="Professional Services, MSA, and Renewal MSA. Flat rate on the GP fulfilled that month. No accumulator, no tiers."
          />
          <StreamNote
            title="FOD"
            rate={pct(rates.fod)}
            color="var(--amber)"
            body={rates.fodHwSwCutover
              ? `Only for deals closed before ${fmtDate(rates.fodHwSwCutover)}: flat rate on FOD GP, paid on its own. FOD on deals closed on/after that date counts as HW/SW and goes toward your tier.`
              : 'Flat rate on FOD GP. Paid on its own and never counted toward your HW/SW tier.'}
          />
        </div>

        {/* Tier ladder */}
        <div style={{ marginTop: '20px' }}>
          <div className="label" style={{ marginBottom: '10px' }}>HW/SW tier ladder (resets every quarter)</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {bands.map(b => (
              <div key={b.label} style={{
                flex: '1 1 140px', padding: '10px 12px',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
              }}>
                <div style={{ fontFamily: 'var(--font-data)', fontWeight: 700, fontSize: '15px', color: 'var(--blue-primary)' }}>
                  {pct(b.rate)}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{b.label}</div>
                <div style={{ fontSize: '11px', fontFamily: 'var(--font-data)', color: 'var(--text-secondary)', marginTop: '3px', whiteSpace: 'nowrap' }}>
                  {bandRange(b)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>}

      {drifted.length > 0 && (
        <Notice tone="amber">
          <strong>{drifted.map(m => m.label).join(', ')}</strong>{' '}
          {drifted.length === 1 ? 'was' : 'were'} approved before the latest sync, so the
          approved amount is locked and no longer recalculates. The steps below show what
          today's data would produce; the “Finance has” figure is what actually pays.
          Ask Finance to re-open the month if the difference looks wrong.
        </Notice>
      )}

      {active.length === 0
        ? <Notice tone="muted">Nothing has been credited to you in {quarter} yet — no shipments have reached a stamped fulfillment date.</Notice>
        : active.map(m => <MonthMath key={m.month} m={m} rates={rates} />)}

      {/* Quarter roll-up */}
      <div className="card" style={{ padding: '22px 24px', borderColor: 'var(--border-bright)' }}>
        <div className="label" style={{ marginBottom: '16px' }}>{quarter} total</div>
        <div style={{ display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          <Figure label="HW/SW"    value={fmt(totals.hwSwPayout)} />
          <Figure label="Services" value={fmt(totals.servicesPayout)} />
          <Figure label="FOD"      value={fmt(totals.fodPayout)} />
          {totals.msPayout > 0 && <Figure label="Managed Services plan" value={fmt(totals.msPayout)} />}
          <Figure label="Quarter commission" value={fmt(totals.totalPayout)} accent="green" big />
        </div>
        {/* Per-row rounding can differ from the live sum by a cent or two — only flag real drift. */}
        {Math.abs(persistedTotal - totals.totalPayout) > 0.05 && (
          <div style={{ marginTop: '14px', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
            Finance's recorded total for {quarter} is{' '}
            <strong style={{ fontFamily: 'var(--font-data)', color: 'var(--text-primary)' }}>{fmt(persistedTotal)}</strong>
            {' '}— see the note above.
          </div>
        )}
      </div>
    </section>
  );
}

// One month, walked step by step.
function MonthMath({ m, rates }) {
  const fodCutover = rates?.fodHwSwCutover;
  const { hwSw, services, fod, ms } = m;
  const hasHwSw     = hwSw.gpThisMonth > 0 || hwSw.payout > 0 || hwSw.cumulativeQTD > 0;
  const hasServices = services.gp > 0;
  const hasFod      = fod.gp > 0;
  const hasMs       = !!ms && ms.gp > 0;
  const hasAe       = hasHwSw || hasServices || hasFod;

  return (
    <div className="card" style={{ marginBottom: '18px', overflow: 'hidden' }}>
      {/* Month header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '16px', flexWrap: 'wrap',
        background: 'var(--bg-surface)',
      }}>
        <h3 style={{ fontSize: '1.05rem' }}>{m.label}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          {m.persisted && (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Finance has <strong style={{ fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>{fmt(m.persisted.totalPayout)}</strong>
              {m.persisted.payDate && <> · pays {fmtDate(m.persisted.payDate)}</>}
            </div>
          )}
          {m.persisted && <StatusBadge status={m.persisted.status} />}
          <div style={{
            fontFamily: 'var(--font-data)', fontSize: '1.3rem', fontWeight: 700,
            color: 'var(--green)', letterSpacing: '-0.02em',
          }}>
            {fmt(m.totalPayout)}
          </div>
        </div>
      </div>

      <div style={{ padding: '4px 0' }}>
        {/* ── HW/SW ── */}
        {hasHwSw && (
          <StreamBlock title="HW/SW" tag={`${hwSw.tierLabel} · ${pct(hwSw.rate)}`} color="var(--blue-primary)">
            <Step n="1" desc={`HW/SW GP fulfilled in ${m.label}`} value={fmt(hwSw.gpThisMonth)} />
            <Step
              n="2"
              desc="Cumulative HW/SW GP, quarter to date"
              detail={`${fmt(hwSw.cumulativeBefore)} carried in  +  ${fmt(hwSw.gpThisMonth)} this month`}
              value={fmt(hwSw.cumulativeQTD)}
            />
            <Step
              n="3"
              desc={`Your tier at ${fmt(hwSw.cumulativeQTD)}`}
              detail={`${hwSw.tierLabel} band`}
              value={pct(hwSw.rate)}
            />
            <Step
              n="4"
              desc="Total HW/SW earned this quarter"
              detail={`${pct(hwSw.rate)} × ${fmt(hwSw.cumulativeQTD)}`}
              value={fmt(hwSw.totalEarnedQTD)}
            />
            <Step
              n="5"
              desc="Less HW/SW already earned earlier this quarter"
              value={`− ${fmt(hwSw.alreadyEarned)}`}
            />
            <Step total desc={`${m.label} HW/SW payout`} value={fmt(hwSw.payout)} />

            {hwSw.crossedTier && (
              <Callout>
                <strong>{m.label} crossed a tier.</strong> Going from {pct(hwSw.priorRate)} to{' '}
                {pct(hwSw.rate)} re-rated <em>all</em> {fmt(hwSw.cumulativeQTD)} of quarter GP at{' '}
                {pct(hwSw.rate)} — not just this month's {fmt(hwSw.gpThisMonth)}. That's why step 4
                is larger than {pct(hwSw.rate)} of this month alone: step 5 subtracts what you were
                already paid at the lower rate, and the difference lands in this check.
              </Callout>
            )}
          </StreamBlock>
        )}

        {/* ── Services ── */}
        {hasServices && (
          <StreamBlock title="Services & Managed Services" tag={`flat ${pct(services.rate)}`} color="var(--green)">
            {services.professionalGp > 0 && <Step desc="Professional Services GP" value={fmt(services.professionalGp)} />}
            {services.msaGp > 0          && <Step desc="Managed Services (MSA) GP" value={fmt(services.msaGp)} />}
            {services.renewalMsaGp > 0   && <Step desc="Renewal MSA GP" value={fmt(services.renewalMsaGp)} />}
            <Step
              desc={`${pct(services.rate)} of ${fmt(services.gp)}`}
              detail="Flat rate — no tier, no accumulator"
              value={fmt(services.payout)}
              total
            />
          </StreamBlock>
        )}

        {/* ── FOD ── */}
        {hasFod && (
          <StreamBlock title="FOD" tag={`flat ${pct(fod.rate)}`} color="var(--amber)">
            <Step desc="FOD GP fulfilled this month" value={fmt(fod.gp)} />
            <Step
              desc={`${pct(fod.rate)} of ${fmt(fod.gp)}`}
              detail={fodCutover ? `Deal closed before ${fmtDate(fodCutover)} — paid separately, not counted toward your HW/SW tier` : 'Paid separately — not counted toward your HW/SW tier'}
              value={fmt(fod.payout)}
              total
            />
          </StreamBlock>
        )}

        {/* ── Managed Services plan (flat rates) ── */}
        {hasMs && (
          <StreamBlock title="Managed Services plan" tag="flat rates" color="var(--blue-sky)">
            {ms.newMsa.gp > 0     && <Step desc="New MSA GP"       detail={`${pct(ms.newMsa.rate)} × ${fmt(ms.newMsa.gp)}`}         value={fmt(ms.newMsa.payout)} />}
            {ms.renewalMsa.gp > 0 && <Step desc="Renewal MSA GP"   detail={`${pct(ms.renewalMsa.rate)} × ${fmt(ms.renewalMsa.gp)}`} value={fmt(ms.renewalMsa.payout)} />}
            {ms.services.gp > 0   && <Step desc="Services GP"      detail={`${pct(ms.services.rate)} × ${fmt(ms.services.gp)}`}     value={fmt(ms.services.payout)} />}
            {ms.hwsw.gp > 0       && <Step desc="HW/SW GP"         detail={`${pct(ms.hwsw.rate)} × ${fmt(ms.hwsw.gp)} — flat, no tier`} value={fmt(ms.hwsw.payout)} />}
            {ms.fod.gp > 0        && <Step desc="FOD GP"           detail={`${pct(ms.fod.rate)} × ${fmt(ms.fod.gp)}`}               value={fmt(ms.fod.payout)} />}
            <Step total desc={`${m.label} Managed Services plan payout`} value={fmt(ms.total)} />
          </StreamBlock>
        )}

        {/* ── Month total ── */}
        <div style={{
          margin: '4px 24px 16px',
          padding: '14px 18px',
          background: 'var(--blue-subtle)',
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius-md)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
            {m.label} total
          </span>
          <span style={{ fontFamily: 'var(--font-data)', fontSize: '1.15rem', fontWeight: 700, color: 'var(--green)' }}>
            {hasAe && !hasMs && <>{fmt(hwSw.payout)} + {fmt(services.payout)} + {fmt(fod.payout)} = {fmt(m.totalPayout)}</>}
            {hasAe && hasMs  && <>AE {fmt(m.aePayout)} + MS plan {fmt(ms.total)} = {fmt(m.totalPayout)}</>}
            {!hasAe && hasMs && <>{fmt(ms.total)}</>}
            {!hasAe && !hasMs && <>{fmt(m.totalPayout)}</>}
          </span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MY SANDBOX
// ═══════════════════════════════════════════════════════════════════════════════
//
// Two ways to build a scenario, and they stack:
//   1. Tick pending shipments and say which month each would land in.
//   2. Add GP by hand for a deal you're chasing that isn't booked yet.
//
// The combined GP per month is POSTed to the backend, which replays the quarter
// through the real engine and returns baseline vs scenario.

function MySandbox({ quarter, repId, math }) {
  const { data: pendingData, loading: pendLoading } = useApi(() => getRepPending(repId), [repId]);
  const monthKeys = useMemo(() => math.months.map(m => ({ key: m.month, label: m.label })), [math]);

  // Pending opps the rep has pulled in: { [oppId]: month it lands in }
  const [pulledIn, setPulledIn] = useState({});
  // Hand-entered deals: { id, month, stream, gp, note }
  const [customDeals, setCustomDeals] = useState([]);

  const pending = pendingData?.pending || [];

  // Default landing month for a pending opp: its estimated ship month when that
  // falls inside the quarter, otherwise the last month of the quarter (the most
  // conservative assumption — latest possible credit).
  const defaultMonth = useCallback((p) => {
    const est = p.estimated_ship_date ? String(p.estimated_ship_date).slice(0, 7) : null;
    if (est && monthKeys.some(m => m.key === est)) return est;
    return monthKeys[monthKeys.length - 1].key;
  }, [monthKeys]);

  const togglePending = (p) => {
    setPulledIn(prev => {
      const next = { ...prev };
      if (next[p.opp_id]) delete next[p.opp_id];
      else next[p.opp_id] = defaultMonth(p);
      return next;
    });
  };

  // Roll the two input sources into one adds-per-month payload. Streams prefixed
  // "msa." land in the Managed Services plan bucket (flat rates); the rest in the
  // AE plan. A pending opp follows the plan its close date puts it on; a hand-
  // entered deal follows the rep's current plan.
  const adds = useMemo(() => {
    const out = {};
    const bump = (month, stream, gp) => {
      if (!month || !gp) return;
      if (!out[month]) out[month] = { hwSwGp: 0, servicesGp: 0, fodGp: 0, msa: { hwSwGp: 0, servicesGp: 0, newMsaGp: 0, renewalMsaGp: 0, fodGp: 0 } };
      if (stream.startsWith('msa.')) out[month].msa[stream.slice(4)] += gp;
      else out[month][stream] += gp;
    };

    for (const p of pending) {
      const month = pulledIn[p.opp_id];
      if (!month) continue;
      const pre = p.plan === 'msa' ? 'msa.' : '';
      bump(month, pre + 'hwSwGp',     parseFloat(p.pending_hwsw_gp)     || 0);
      bump(month, pre + 'servicesGp', parseFloat(p.pending_services_gp) || 0);
      bump(month, pre + 'fodGp',      parseFloat(p.pending_fod_gp)      || 0);
    }
    for (const d of customDeals) {
      bump(d.month, d.stream, parseFloat(d.gp) || 0);
    }
    return out;
  }, [pending, pulledIn, customDeals]);

  const hasScenario = Object.keys(adds).length > 0;

  // Run the scenario against the backend. Debounced so typing in a GP field
  // doesn't fire a request per keystroke.
  const [result, setResult]   = useState(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);
  const addsKey = JSON.stringify(adds);

  useEffect(() => {
    // Nothing picked yet — don't spend a round trip proving baseline == baseline.
    if (JSON.parse(addsKey) && Object.keys(JSON.parse(addsKey)).length === 0) {
      setResult(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setRunning(true);
      setRunError(null);
      try {
        const r = await runRepSandbox(quarter, JSON.parse(addsKey), repId);
        if (!cancelled) setResult(r);
      } catch (err) {
        if (!cancelled) setRunError(err.message);
      } finally {
        if (!cancelled) setRunning(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [addsKey, quarter, repId]);

  const reset = () => { setPulledIn({}); setCustomDeals([]); };

  const pulledCount = Object.keys(pulledIn).length;
  const splitEstimated = pending.some(p => p.split_estimated && pulledIn[p.opp_id]);

  return (
    <section>
      <SectionHeading
        eyebrow="What if"
        title="My Sandbox"
        blurb={`Pull pending shipments into a month, or pencil in a deal you're chasing, and see
                what ${quarter} would pay. Nothing here is saved and nothing affects your real
                commission — it's a scratch pad for planning your quarter.`}
      />

      {/* ── Input 1: pending shipments ── */}
      <div className="card" style={{ marginBottom: '18px', overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ fontSize: '1rem' }}>Pull in pending shipments</h3>
            <div className="label" style={{ marginTop: '4px' }}>
              Closed-won work that hasn't hit a fulfillment date yet. Tick it and choose the month it lands in.
            </div>
          </div>
          {pulledCount > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={reset}>Clear scenario</button>
          )}
        </div>

        {pendLoading
          ? <RowSkeleton rows={3} />
          : pending.length === 0
            ? <Empty>No pending shipments — everything closed-won has already been credited.</Empty>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '36px' }}></th>
                      <th>Opportunity</th>
                      <th style={{ textAlign: 'right' }}>HW/SW GP</th>
                      <th style={{ textAlign: 'right' }}>Services GP</th>
                      <th style={{ textAlign: 'right' }}>FOD GP</th>
                      <th>Est. ship</th>
                      <th>Lands in</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map(p => {
                      const on = !!pulledIn[p.opp_id];
                      return (
                        <tr key={p.opp_id} style={{ background: on ? 'var(--blue-subtle)' : undefined }}>
                          <td>
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => togglePending(p)}
                              aria-label={`Include ${p.opp_number} in scenario`}
                              style={{ cursor: 'pointer', width: '15px', height: '15px', accentColor: 'var(--blue-primary)' }}
                            />
                          </td>
                          <td>
                            <div style={{ color: 'var(--text-primary)', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.opp_title || '—'}
                            </div>
                            <div style={{ fontFamily: 'var(--font-data)', fontSize: '11px', color: 'var(--text-accent)' }}>
                              {p.opp_number}{p.customer_name ? ` · ${p.customer_name}` : ''}
                            </div>
                          </td>
                          <td className="td-number">{fmt(p.pending_hwsw_gp)}</td>
                          <td className="td-number">{fmt(p.pending_services_gp)}</td>
                          <td className="td-number">{fmt(p.pending_fod_gp)}</td>
                          <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{fmtDate(p.estimated_ship_date)}</td>
                          <td>
                            <select
                              className="input"
                              disabled={!on}
                              value={pulledIn[p.opp_id] || defaultMonth(p)}
                              onChange={e => setPulledIn(prev => ({ ...prev, [p.opp_id]: e.target.value }))}
                              style={{ width: 'auto', minWidth: '110px', padding: '5px 8px', fontSize: '13px', opacity: on ? 1 : 0.4 }}
                            >
                              {monthKeys.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

        {splitEstimated && (
          <div style={{ padding: '0 24px 16px' }}>
            <Notice tone="muted" tight>
              One or more of the opportunities you picked hasn't had its GP split by stream yet
              (that happens on the next NetSuite sync). Until then its full GP is treated as HW/SW,
              so a services-heavy deal will read low here.
            </Notice>
          </div>
        )}
      </div>

      {/* ── Input 2: hand-entered deals ── */}
      <CustomDeals
        deals={customDeals}
        setDeals={setCustomDeals}
        monthKeys={monthKeys}
        rates={math.rates}
        plan={math.plan}
      />

      {/* ── Result ── */}
      {runError && <Notice tone="red">Couldn't run the scenario: {runError}</Notice>}

      {!hasScenario ? (
        <Notice tone="muted">
          Pick a pending shipment above, or add a deal, to see the what-if.
        </Notice>
      ) : result ? (
        <ScenarioResult result={result} running={running} />
      ) : (
        <RowSkeleton rows={4} />
      )}
    </section>
  );
}

// ─── Hand-entered hypothetical deals ──────────────────────────────────────────
// Stream choices depend on the plan a deal closed today would land on.
// No separate FOD choice on the AE plan: a deal closed today is past the FOD cutover,
// so its FOD counts as HW/SW (pre-cutover pending opps still carry their own FOD).
const AE_STREAMS = [
  { key: 'hwSwGp',     label: 'HW/SW (incl. FOD)' },
  { key: 'servicesGp', label: 'Services / MSA' },
];
const MS_STREAMS = [
  { key: 'msa.newMsaGp',     label: 'New MSA (25%)' },
  { key: 'msa.renewalMsaGp', label: 'Renewal MSA (8%)' },
  { key: 'msa.servicesGp',   label: 'Services (16%)' },
  { key: 'msa.hwSwGp',       label: 'HW/SW (5%)' },
  { key: 'msa.fodGp',        label: 'FOD (5%)' },
];

function CustomDeals({ deals, setDeals, monthKeys, rates, plan }) {
  const [nextId, setNextId] = useState(1);
  const onMs = plan?.current === 'msa';
  const STREAMS = onMs ? MS_STREAMS : AE_STREAMS;

  const add = () => {
    setDeals(d => [...d, {
      id: nextId,
      month: monthKeys[monthKeys.length - 1].key,
      stream: STREAMS[0].key,
      gp: '',
      note: '',
    }]);
    setNextId(n => n + 1);
  };

  const update = (id, patch) => setDeals(d => d.map(x => x.id === id ? { ...x, ...patch } : x));
  const remove = (id) => setDeals(d => d.filter(x => x.id !== id));

  return (
    <div className="card" style={{ marginBottom: '18px', overflow: 'hidden' }}>
      <div style={{ padding: '18px 24px', borderBottom: deals.length ? '1px solid var(--border)' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ fontSize: '1rem' }}>Add a deal you're chasing</h3>
          <div className="label" style={{ marginTop: '4px' }}>
            Enter GP — not revenue — for something not yet closed-won.{' '}
            {onMs ? 'A deal closed today lands on your Managed Services plan (flat rates).' : `Services and MSA both pay ${pct(rates.services)}.`}
          </div>
        </div>
        <button className="btn btn-sm" onClick={add}>+ Add GP</button>
      </div>

      {deals.length > 0 && (
        <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {deals.map(d => (
            <div key={d.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                className="input"
                placeholder="What is it? (optional)"
                value={d.note}
                onChange={e => update(d.id, { note: e.target.value })}
                style={{ flex: '1 1 180px', minWidth: '150px' }}
              />
              <select
                className="input"
                value={d.stream}
                onChange={e => update(d.id, { stream: e.target.value })}
                style={{ width: 'auto', minWidth: '140px' }}
              >
                {STREAMS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <select
                className="input"
                value={d.month}
                onChange={e => update(d.id, { month: e.target.value })}
                style={{ width: 'auto', minWidth: '110px' }}
              >
                {monthKeys.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
              <div style={{ position: 'relative', flex: '0 0 150px' }}>
                <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontFamily: 'var(--font-data)', fontSize: '13px', pointerEvents: 'none' }}>$</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="1000"
                  placeholder="GP amount"
                  value={d.gp}
                  onChange={e => update(d.id, { gp: e.target.value })}
                  style={{ width: '100%', paddingLeft: '24px', fontFamily: 'var(--font-data)' }}
                />
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => remove(d.id)}
                aria-label="Remove this deal"
                style={{ color: 'var(--text-muted)' }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Scenario result ──────────────────────────────────────────────────────────
function ScenarioResult({ result, running }) {
  const { baseline, scenario, delta, quarter, rates } = result;

  const showMs = scenario.months.some(m => m.ms?.gp > 0) || baseline.months.some(m => m.ms?.gp > 0);

  return (
    <div style={{ opacity: running ? 0.55 : 1, transition: 'opacity 0.15s' }}>
      {/* Headline delta */}
      <div style={{
        background: 'linear-gradient(135deg, var(--blue-deeper) 0%, var(--blue-dark) 100%)',
        border: '1px solid var(--border-bright)',
        borderRadius: 'var(--radius-xl)',
        padding: '26px 32px',
        marginBottom: '18px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '24px', flexWrap: 'wrap',
        boxShadow: 'var(--shadow-glow)',
      }}>
        <div>
          <div className="label" style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
            What-if {quarter} commission
          </div>
          <div style={{ fontSize: '2.6rem', fontWeight: 700, fontFamily: 'var(--font-data)', color: 'white', letterSpacing: '-0.03em', lineHeight: 1 }}>
            {fmt(scenario.totals.totalPayout)}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13.5px', marginTop: '8px' }}>
            vs {fmt(baseline.totals.totalPayout)} as things stand today
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="label" style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>Difference</div>
          <div style={{
            fontSize: '2rem', fontWeight: 700, fontFamily: 'var(--font-data)',
            color: delta.totalPayout >= 0.005 ? '#7ee6a8' : 'white',
            letterSpacing: '-0.02em', lineHeight: 1,
          }}>
            {fmtDelta(delta.totalPayout)}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '12px', marginTop: '8px', fontFamily: 'var(--font-data)' }}>
            HW/SW {fmtDelta(delta.hwSwPayout)} · Svc {fmtDelta(delta.servicesPayout)} · FOD {fmtDelta(delta.fodPayout)}
          </div>
        </div>
      </div>

      {/* Month-by-month comparison */}
      <div className="card" style={{ marginBottom: '18px', overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: '1rem' }}>Month by month</h3>
          <div className="label" style={{ marginTop: '4px' }}>
            Because HW/SW accumulates, GP added early can re-rate the months after it too.
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Month</th>
                <th style={{ textAlign: 'right' }}>HW/SW GP added</th>
                <th style={{ textAlign: 'right' }}>Cumulative HW/SW</th>
                <th>Tier</th>
                {showMs && <th style={{ textAlign: 'right' }}>MS plan payout</th>}
                <th style={{ textAlign: 'right' }}>Payout today</th>
                <th style={{ textAlign: 'right' }}>What-if payout</th>
                <th style={{ textAlign: 'right' }}>Difference</th>
              </tr>
            </thead>
            <tbody>
              {scenario.months.map((s, i) => {
                const b = baseline.months[i];
                const d = s.totalPayout - b.totalPayout;
                const tierMoved = s.hwSw.rate !== b.hwSw.rate;
                return (
                  <tr key={s.month}>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{s.label}</td>
                    <td className="td-number" style={{ color: s.hwSw.gpThisMonthAdded > 0 ? 'var(--blue-primary)' : 'var(--text-muted)' }}>
                      {s.hwSw.gpThisMonthAdded > 0 ? `+${fmt(s.hwSw.gpThisMonthAdded)}` : '—'}
                    </td>
                    <td className="td-number">{fmt(s.hwSw.cumulativeQTD)}</td>
                    <td>
                      <span style={{ fontFamily: 'var(--font-data)', fontSize: '12.5px', color: tierMoved ? 'var(--amber)' : 'var(--text-secondary)' }}>
                        {s.hwSw.tierLabel} · {pct(s.hwSw.rate)}
                      </span>
                      {tierMoved && (
                        <span style={{ fontSize: '11px', color: 'var(--amber)', marginLeft: '6px' }}>
                          ↑ from {pct(b.hwSw.rate)}
                        </span>
                      )}
                    </td>
                    {showMs && (
                      <td className="td-number" style={{ color: s.ms.total !== b.ms.total ? 'var(--blue-sky)' : 'var(--text-secondary)' }}>
                        {fmt(s.ms.total)}
                      </td>
                    )}
                    <td className="td-number" style={{ color: 'var(--text-secondary)' }}>{fmt(b.totalPayout)}</td>
                    <td className="td-number" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{fmt(s.totalPayout)}</td>
                    <td className="td-number" style={{ color: d >= 0.005 ? 'var(--green)' : d <= -0.005 ? 'var(--red)' : 'var(--text-muted)', fontWeight: 600 }}>
                      {fmtDelta(d)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: 'var(--bg-elevated)' }}>
                <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Quarter</td>
                <td className="td-number" style={{ color: 'var(--blue-primary)' }}>
                  {scenario.totals.hwSwGp - baseline.totals.hwSwGp > 0
                    ? `+${fmt(scenario.totals.hwSwGp - baseline.totals.hwSwGp)}` : '—'}
                </td>
                <td></td>
                <td></td>
                {showMs && <td className="td-number" style={{ color: 'var(--blue-sky)' }}>{fmt(scenario.totals.msPayout)}</td>}
                <td className="td-number" style={{ color: 'var(--text-secondary)' }}>{fmt(baseline.totals.totalPayout)}</td>
                <td className="td-number" style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(scenario.totals.totalPayout)}</td>
                <td className="td-number" style={{ fontWeight: 700, color: delta.totalPayout >= 0.005 ? 'var(--green)' : 'var(--text-muted)' }}>
                  {fmtDelta(delta.totalPayout)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* The same step-by-step math, for the scenario */}
      <details className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <summary style={{
          padding: '18px 24px', cursor: 'pointer',
          fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)',
          fontFamily: 'var(--font-ui)',
        }}>
          Show the what-if math, step by step
        </summary>
        <div style={{ borderTop: '1px solid var(--border)', padding: '18px 0 4px' }}>
          {scenario.months
            .filter(m => m.hwSw.gpThisMonth > 0 || m.services.gp > 0 || m.fod.gp > 0 || m.ms?.gp > 0)
            .map(m => <MonthMath key={m.month} m={m} rates={rates} />)}
        </div>
      </details>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Presentational bits
// ═══════════════════════════════════════════════════════════════════════════════
function SectionHeading({ eyebrow, title, blurb }) {
  return (
    <div style={{ marginBottom: '18px' }}>
      <div className="label" style={{ marginBottom: '6px' }}>{eyebrow}</div>
      <h2 style={{ fontSize: '1.3rem', marginBottom: '8px' }}>{title}</h2>
      <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', maxWidth: '760px', lineHeight: 1.6, margin: 0 }}>
        {blurb}
      </p>
    </div>
  );
}

function StreamNote({ title, rate, color, body }) {
  return (
    <div style={{
      padding: '14px 16px',
      background: 'var(--bg-elevated)',
      borderLeft: `3px solid ${color}`,
      borderRadius: 'var(--radius-sm)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: '6px' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
        <span style={{ fontFamily: 'var(--font-data)', fontSize: '12.5px', fontWeight: 700, color }}>{rate}</span>
      </div>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

// A titled group of calculation steps within one month.
function StreamBlock({ title, tag, color, children }) {
  return (
    <div style={{ padding: '16px 24px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <span style={{ width: '3px', height: '14px', background: color, borderRadius: '2px' }} />
        <span style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-primary)' }}>
          {title}
        </span>
        <span style={{ fontFamily: 'var(--font-data)', fontSize: '11.5px', color, background: 'var(--bg-elevated)', padding: '2px 8px', borderRadius: '10px' }}>
          {tag}
        </span>
      </div>
      {children}
    </div>
  );
}

// One line of arithmetic. `total` renders it as the stream's bottom line.
function Step({ n, desc, detail, value, total }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: '12px',
      padding: total ? '10px 0 4px' : '6px 0',
      borderTop: total ? '1px solid var(--border)' : 'none',
      marginTop: total ? '6px' : 0,
    }}>
      {n && (
        <span style={{
          flex: '0 0 20px', height: '20px', lineHeight: '20px', textAlign: 'center',
          fontSize: '10.5px', fontWeight: 700, fontFamily: 'var(--font-data)',
          color: 'var(--text-muted)', background: 'var(--bg-elevated)', borderRadius: '4px',
        }}>
          {n}
        </span>
      )}
      {!n && <span style={{ flex: '0 0 20px' }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13.5px', color: total ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: total ? 600 : 400 }}>
          {desc}
        </div>
        {detail && (
          <div style={{ fontSize: '11.5px', fontFamily: 'var(--font-data)', color: 'var(--text-muted)', marginTop: '2px' }}>
            {detail}
          </div>
        )}
      </div>
      <div style={{
        fontFamily: 'var(--font-data)', whiteSpace: 'nowrap',
        fontSize: total ? '15px' : '13.5px',
        fontWeight: total ? 700 : 500,
        color: total ? 'var(--green)' : 'var(--text-primary)',
      }}>
        {value}
      </div>
    </div>
  );
}

function Callout({ children }) {
  return (
    <div style={{
      margin: '12px 0 4px',
      padding: '12px 16px',
      background: 'var(--amber-subtle)',
      borderLeft: '3px solid var(--amber)',
      borderRadius: 'var(--radius-sm)',
      fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.6,
    }}>
      {children}
    </div>
  );
}

function Figure({ label, value, accent, big }) {
  return (
    <div>
      <div className="label" style={{ marginBottom: '6px' }}>{label}</div>
      <div style={{
        fontFamily: 'var(--font-data)',
        fontSize: big ? '1.5rem' : '1.15rem',
        fontWeight: big ? 700 : 600,
        color: accent === 'green' ? 'var(--green)' : 'var(--text-primary)',
        letterSpacing: '-0.02em',
      }}>
        {value}
      </div>
    </div>
  );
}

function Notice({ tone = 'muted', tight, children }) {
  const map = {
    amber: { bg: 'var(--amber-subtle)', bd: 'var(--amber)' },
    red:   { bg: 'var(--red-subtle)',   bd: 'var(--red)' },
    muted: { bg: 'var(--bg-elevated)',  bd: 'var(--border-bright)' },
  };
  const { bg, bd } = map[tone] || map.muted;
  return (
    <div style={{
      background: bg,
      borderLeft: `3px solid ${bd}`,
      borderRadius: 'var(--radius-sm)',
      padding: tight ? '10px 14px' : '14px 18px',
      marginBottom: tight ? 0 : '18px',
      fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6,
    }}>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return (
    <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid:     { cls: 'badge-paid',     label: 'Paid' },
    approved: { cls: 'badge-approved', label: 'Approved' },
    pending:  { cls: 'badge-pending',  label: 'Pending' },
    disputed: { cls: 'badge-disputed', label: 'Disputed' },
    clawback: { cls: 'badge-disputed', label: 'Clawback' },
  };
  const { cls, label } = map[status] || { cls: '', label: status };
  return <span className={`badge ${cls}`}>{label}</span>;
}

function RowSkeleton({ rows = 3 }) {
  return (
    <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height: '36px', borderRadius: '6px' }} />
      ))}
    </div>
  );
}

function CalcSkeleton() {
  return (
    <div>
      <div className="skeleton" style={{ height: '28px', width: '220px', marginBottom: '10px' }} />
      <div className="skeleton" style={{ height: '16px', width: '480px', marginBottom: '24px' }} />
      <div className="skeleton" style={{ height: '190px', borderRadius: '16px', marginBottom: '18px' }} />
      <div className="skeleton" style={{ height: '260px', borderRadius: '16px', marginBottom: '18px' }} />
      <div className="skeleton" style={{ height: '260px', borderRadius: '16px' }} />
    </div>
  );
}
